import type { GetShortCodeResponseType } from "@/domain/verifyApi";

export type OnboardingEntryPointType =
  NonNullable<GetShortCodeResponseType>["entryPointType"];

// Identifies which content a step block renders (its copy and awareness rules),
// independently of where a variant places it in the flow.
export enum OnboardingStepEnum {
  INTRO = "INTRO",
  BENEFITS = "BENEFITS",
  CONCERNS = "CONCERNS",
  NEXT_STEPS = "NEXT_STEPS",
}

export const MONTHS_TO_TURNING_65_SUBTRACTION_DELTA = 3;
export const AGING_INTO_MEDICARE_MONTHS_TO_AWARENESS_DELTA = 3;
export const MONTH_DAYS_DELTA = 30;

// Steps fill the whole screen, under the floating header and footer controls,
// so each step paints its own background there and keeps its content clear of
// `headerHeight` (top) and `footerHeight` (bottom).
export type OnboardingScreenType = {
  width: number;
  height: number;
  headerHeight: number;
  footerHeight: number;
};

// The color of what a step paints under the footer controls, so they stay
// readable: "light" for the default background, "dark" for photos or maroon.
export type OnboardingFooterToneType = "light" | "dark";

export enum OnboardingConcernOptionEnum {
  PAYING_FOR_DOCTOR,
  NOT_KNOWING_IM_COVERED,
  NOT_KNOWING_HOW_TO_GET_COVERED,
  PAPERWORK,
  SOMETHING_ELSE,
  UNEXPECTED_BILLS,
  MAIL,
  MISSING_ENROLLMENT_DEADLINE,
  TOO_MANY_MEDICARE_ADS_AND_CALLS,
  KEEPING_MY_CURRENT_DOCTORS,
  SUBMITTING_RENEWALS,
  FIGURING_OUT_WHEN_TO_RENEW,
  PART_A_AND_B,
  PART_A_B_AND_D,
  PART_C,
  IM_NOT_SURE,
  MEETING_REQUIREMENTS,
  FIGURING_OUT_WHEN_TO_SUBMIT_REQUIREMENTS,
}

// Each id must match its folder in `blocks/OnboardingVariants/`, its key under
// `onboarding.variants` in native.json, and the variant value set in Mixpanel.
export const ONBOARDING_VARIANT_IDS = [
  "default",
  "noConcernsStep",
  "introStepWithImage",
  "improvedVerbiage",
  "allStepsWithImages",
] as const;

export type OnboardingVariantIdType = (typeof ONBOARDING_VARIANT_IDS)[number];

export const DEFAULT_ONBOARDING_VARIANT_ID: OnboardingVariantIdType = "default";

// Past this, onboarding renders the default variant rather than waiting on Mixpanel.
export const ONBOARDING_VARIANT_TIMEOUT_MS = 5000;

// Mixpanel values are free text typed by Pointcare, so anything that isn't a
// known id falls back to the default variant instead of breaking onboarding.
export const resolveOnboardingVariantId = (
  value: unknown,
): OnboardingVariantIdType =>
  ONBOARDING_VARIANT_IDS.find(id => id === value) ??
  DEFAULT_ONBOARDING_VARIANT_ID;

// AD_HOC_SELF_PAY uses the same onboarding flow and translations as
// SELF_PAY_CONVERSION.
export const getOnboardingFlowEntryPointType = (
  entryPointType: OnboardingEntryPointType,
): OnboardingEntryPointType =>
  entryPointType === "AD_HOC_SELF_PAY" ? "SELF_PAY_CONVERSION" : entryPointType;

// One Mixpanel flag per entry point, so each entry point can run its own
// experiment independently.
export const getOnboardingVariantFlagKey = (
  entryPointType: OnboardingEntryPointType,
) =>
  `onboarding_variant_${getOnboardingFlowEntryPointType(entryPointType).toLowerCase()}`;
